import { get } from 'lodash';
import {
  calculateTotalWithMultiplier,
  calculateTotalWithCustomSections,
  calculateTotalWithoutMultiplier,
  calculateTotalForFileSharingAndCollaboration
} from './utils';

export const schema = [
  {
    categoryName: 'Customer Success (Relationship)',
    categoryId: 'customerSuccessRelationship',
    isFinalTotalVisible:true,
    categoryTotalCalculator: sectionTotals => {
      return Object.values(sectionTotals).reduce(
        (accumulator, currentArray) => {
          accumulator[0] += currentArray[0];
          accumulator[1] += currentArray[1];
          return accumulator;
        },
        [0, 0]
      );
    },
    sections: [
      {
        id: 'customerSuccessRelationship',
        defaultTotals: [0, 0],
        customGlobalValidation: (selections, numberSelections) => {
          const relationshipLevel = get(
            selections,
            'customerSuccessRelationship.customerSuccessRelationship.relationshipLevel',
            ''
          );
          const numberOfUsers = +get(
            numberSelections,
            'managedUserAndEndpoints.userSecurityAndManagement.numberOfUsers',
            0
          );
          if (
            relationshipLevel === 'Low - Monthly SDR, Annual Business Review' &&
            numberOfUsers > 20
          ) {
            return 'Low customer relationship level can only be selected for 20 or less users.';
          }
          return false;
        },
        items: [
          {
            label: 'Choose the relationship level*',
            id: 'relationshipLevel',
            options: [
              {
                label: 'Low - Monthly SDR, Annual Business Review',
                value: 'Low - Monthly SDR, Annual Business Review',
                additionalData: {
                  cost: 108,
                  price: 216
                }
              },
              {
                label: 'Standard - Monthly SDR, Quarterly Business Review',
                value: 'Standard - Monthly SDR, Quarterly Business Review',
                additionalData: {
                  cost: 307.5,
                  price: 615.0
                }
              },
              {
                label: 'Premium / Legal',
                value: 'Premium / Legal',
                additionalData: {
                  cost: 865.5,
                  price: 1731.0
                }
              }
            ],
            rowTotalConfig: ['cost', 'price']
          }
        ],
        totalCalculator: calculateTotalWithoutMultiplier
      }
    ]
  },

  {
    categoryName: 'Managed User & Endpoints',
    categoryId: 'managedUserAndEndpoints',
    isFinalTotalVisible:true,
    categoryTotalCalculator: sectionTotals => {
      return Object.values(sectionTotals).reduce(
        (accumulator, currentArray) => {
          accumulator[0] += currentArray[0];
          accumulator[1] += currentArray[1];
          return accumulator;
        },
        [0, 0]
      );
    },
    sections: [
      {
        title: 'User Security & Management',
        id: 'userSecurityAndManagement',
        defaultTotals: [0, 0],
        numberField: {
          label: 'Number of Users*',
          id: 'numberOfUsers',
          defaultValue: 1,
          quoteLabel: 'Users',
          customValidation: value => {
            return value <= 0
              ? 'Number of Users must be greater than 0'
              : false;
          }
        },
        premiumValidation: (selections) => {
            const relationshipLevel = get(
                selections,
                'customerSuccessRelationship.customerSuccessRelationship.relationshipLevel',
                ''
            );

            if (
                relationshipLevel === 'Premium / Legal'
            ) {
              return true;
            }
            return false;
          },
        items: [
          {
            label: 'What best describes the user identity management?*',
            note:
              'Note: does not include any platform licenses / subscriptions',
            id: 'bestIdentityManagementDescription',
            options: [
              {
                label: 'Entra ID (Azure AD) Only',
                value: 'Entra ID (Azure AD) Only',
                additionalData: {
                  cost: 9.34,
                  price: 18.67
                },
                premiumAdditionalData: {
                    cost: 10.58,
                    price: 21.16
                  }
              },
              {
                label: 'Entra ID (Azure AD) Hybrid',
                value: 'Entra ID (Azure AD) Hybrid',
                additionalData: {
                  cost: 12.45,
                  price: 24.89
                },
                premiumAdditionalData: {
                    cost: 13.69,
                    price: 27.38
                  }
              },
              {
                label: 'Active Directory',
                value: 'Active Directory',
                additionalData: {
                  cost: 15.56,
                  price: 31.12
                },
                premiumAdditionalData: {
                    cost: 16.80,
                    price: 33.60
                  }
              }
            ],
            rowTotalConfig: ['cost', 'price']
          }
        ],
        totalCalculator: calculateTotalWithMultiplier
      },
      {
        title: 'Windows Devices',
        id: 'windowsDevices',
        defaultTotals: [0, 0],
        numberField: {
          label: 'Number of Windows PCs*',
          id: 'numberOfWindowsPcs',
          defaultValue: 1,
          quoteLabel: 'Windows Devices',
          customValidation: (values, selections, numberSelections) => {
            const valueWindows = +get(
              numberSelections,
              'managedUserAndEndpoints.windowsDevices.numberOfWindowsPcs',
              0
            );
            const valueMacs = +get(
              numberSelections,
              'managedUserAndEndpoints.macDevices.numberOfWindowsMacs',
              0
            );
            if (valueMacs === 0 && valueWindows <= 0) {
              return 'Number of Windows PCs must be greater than 0 if number of macs are 0';
            }
            return false;
          }
        },
        premiumValidation: (selections) => {
            const relationshipLevel = get(
                selections,
                'customerSuccessRelationship.customerSuccessRelationship.relationshipLevel',
                ''
            );

            if (
                relationshipLevel === 'Premium / Legal'
            ) {
              return true;
            }
            return false;
          },
        items: [
          {
            label: 'What best describes the Windows PCs?',
            id: 'bestWindowsPCsDescription',
            options: [
              {
                label:
                  'Windows Pro/Business/Enterprise - Physical, less than 5 years old',
                value:
                  'Windows Pro/Business/Enterprise - Physical, less than 5 years old',
                additionalData: {
                  cost: 11.14,
                  price: 22.29
                },
                premiumAdditionalData: {
                    cost: 18.88,
                    price: 37.76
                  }
              },
              {
                label:
                  'Windows Pro/Business/Enterprise - Physical, more than 5 years old',
                value:
                  'Windows Pro/Business/Enterprise - Physical, more than 5 years old',
                additionalData: {
                  cost: 17.37,
                  price: 34.73
                },
                premiumAdditionalData: {
                    cost: 25.11,
                    price: 50.22
                  }
              },
              {
                label: 'Windows - Azure Virtual Desktop only (BYOD computer or physical workstation not under management)',
                value: 'Windows - Azure Virtual Desktop only (BYOD computer or physical workstation not under management)',
                additionalData: {
                  cost: 9.34,
                  price: 18.67
                },
                premiumAdditionalData: {
                    cost: 17.08,
                    price: 34.16
                  }
              }
            ],
            rowTotalConfig: ['cost', 'price']
          },
          {
            label: 'How are Windows devices managed?',
            note:
              'Note: does not include any platform licenses / subscriptions',
            id: 'howAreWindowsDevicesManaged',
            options: [
              {
                label: 'Intune',
                value: 'Intune',
                additionalData: {
                  cost: 6.22,
                  price: 12.45
                }
              },
              {
                label: 'Active Directory GPOs',
                value: 'Active Directory GPOs',
                additionalData: {
                  cost: 9.34,
                  price: 18.67
                }
              },
              {
                label: 'Mix of Intune and Active Directory',
                value: 'Mix of Intune and Active Directory',
                additionalData: {
                  cost: 12.45,
                  price: 24.89
                }
              }
            ],
            rowTotalConfig: ['cost', 'price']
          }
        ],
        totalCalculator: calculateTotalWithMultiplier
      },
      {
        title: 'Mac Devices',
        id: 'macDevices',
        defaultTotals: [0, 0],
        numberField: {
          label: 'Number of Macs*',
          id: 'numberOfWindowsMacs',
          defaultValue: 1,
          quoteLabel: 'Mac Devices',
          customValidation: (values, selections, numberSelections) => {
            const valueWindows = +get(
              numberSelections,
              'managedUserAndEndpoints.windowsDevices.numberOfWindowsPcs',
              0
            );
            const valueMacs = +get(
              numberSelections,
              'managedUserAndEndpoints.macDevices.numberOfWindowsMacs',
              0
            );
            if (valueMacs <= 0 && valueWindows === 0) {
              return 'Number of macs  must be greater than 0 if number of Windows PCs are 0';
            }
            return false;
          }
        },
        premiumValidation: (selections) => {
            const relationshipLevel = get(
                selections,
                'customerSuccessRelationship.customerSuccessRelationship.relationshipLevel',
                ''
            );

            if (
                relationshipLevel === 'Premium / Legal'
            ) {
              return true;
            }
            return false;
          },
        items: [
          {
            label: 'What best describes the Macs?',
            id: 'bestMacsDescription',
            options: [
              {
                label: 'Less than 4 years old',
                value: 'Less than 4 years old',
                additionalData: {
                  cost: 13.96,
                  price: 27.91
                },
                premiumAdditionalData: {
                    cost: 21.70,
                    price: 43.40
                  }
              },
              {
                label: 'More than 4 years old',
                value: 'More than 4 years old',
                additionalData: {
                  cost: 17.07,
                  price: 34.13
                },
                premiumAdditionalData: {
                    cost: 24.81,
                    price: 49.62
                  }
              }
            ],
            rowTotalConfig: ['cost', 'price']
          },
          {
            label: 'How are Mac devices managed?',
            note:
              'Note: does not include any platform licenses / subscriptions',
            id: 'howAreMacDevicesManaged',
            options: [
              {
                label: 'Microsoft Intune',
                value: 'Microsoft Intune',
                additionalData: {
                  cost: 6.22,
                  price: 12.45
                }
              },
              {
                label: 'Jamf',
                value: 'Jamf',
                additionalData: {
                  cost: 9.34,
                  price: 18.67
                }
              }
            ],
            rowTotalConfig: ['cost', 'price']
          }
        ],
        totalCalculator: calculateTotalWithMultiplier
      }
    ]
  },
  {
    categoryName: 'Managed Server',
    categoryId: 'managedServer',
    isFinalTotalVisible:true,
    categoryTotalCalculator: sectionTotals => {
      return Object.values(sectionTotals).reduce(
        (accumulator, currentArray) => {
          accumulator[0] += currentArray[0];
          accumulator[1] += currentArray[1];
          return accumulator;
        },
        [0, 0]
      );
    },
    sections: [
      {
        title: 'Servers - Physical',
        id: 'serversPhysical',
        defaultTotals: [0, 0],
        numberField: {
          label: 'Number of physical servers',
          id: 'numberOfPhysicalServers',
          quoteLabel: 'Physical Servers',
          defaultValue: 1
        },
        items: [
          {
            label: 'What platform are physical servers running?',
            id: 'whatPlatformArePhysicalServersRunning',
            options: [
              {
                label: 'Microsoft Hyper-V',
                value: 'Microsoft Hyper-V',
                additionalData: {
                  cost: 79.86,
                  price: 159.72
                }
              },
              {
                label: 'VMWare',
                value: 'VMWare',
                additionalData: {
                  cost: 102.79,
                  price: 205.58
                }
              },
              {
                label: `Physical servers with no VM's`,
                value: `Physical servers with no VM's`,
                additionalData: {
                  cost: 102.79,
                  price: 205.58
                }
              }
            ],
            rowTotalConfig: ['cost', 'price']
          }
        ],
        totalCalculator: calculateTotalWithMultiplier
      },
      {
        title: 'Servers - Virtual',
        id: 'serversVirtual',
        defaultTotals: [0, 0],
        numberField: {
          label: 'Number of Virtual Servers/Machines (VM)',
          id: 'numberOfVirtualServers',
          defaultValue: 1,
          quoteLabel: 'Virtual Servers'
        },
        items: [
          {
            label: 'What is the oldest OS virtual server running?',
            id: 'oldestOsVirtualServersRunning',
            options: [
              {
                label: 'Windows Server 2022+',
                value: 'Windows Server 2022+',
                additionalData: {
                  cost: 79.86,
                  price: 159.72
                }
              },
              {
                label: 'Windows Server 2019',
                value: 'Windows Server 2019',
                additionalData: {
                  cost: 79.86,
                  price: 159.72
                }
              },
              {
                label: 'Windows Server 2016',
                value: 'Windows Server 2016',
                additionalData: {
                  cost: 104.84,
                  price: 209.68
                }
              },
              {
                label: 'Older than 2016 with an approved remediation project',
                value: 'Older than 2016 with an approved remediation project',
                additionalData: {
                  cost: 104.84,
                  price: 209.68
                }
              }
            ],
            rowTotalConfig: ['cost', 'price']
          },
          {
            label: 'Where are VMs located?',
            note:
              'Note: does not include any hosting licenses / subscriptions / fees',
            id: 'whereAreVmsLocated',
            options: [
              {
                label: 'All in Azure',
                value: 'All in Azure',
                additionalData: {
                  cost: 0,
                  price: 0
                }
              },
              {
                label: 'All in a Private Cloud',
                value: 'All in a Private Cloud',
                additionalData: {
                  cost: 12.49,
                  price: 24.98
                }
              },
              {
                label: 'On-premises',
                value: 'On-premises',
                additionalData: {
                  cost: 16.65,
                  price: 33.31
                }
              },
              {
                label: 'A Mix of Azure, Private Cloud, and/or on-premises',
                value: 'A Mix of Azure, Private Cloud, and/or on-premises',
                additionalData: {
                  cost: 24.98,
                  price: 49.96
                }
              }
            ],
            rowTotalConfig: ['cost', 'price']
          }
        ],
        totalCalculator: calculateTotalWithMultiplier
      },
      {
        title: 'Servers - Remote Access',
        id: 'serversRemoteAccess',
        defaultTotals: [0, 0],
        items: [
          {
            label:
              'Are any server-based resources accessible from outside the office?',
            id: 'areAnyServerBasedResourcesAccessibleFromOutsideTheOffice?',
            options: [
              {
                label: 'No',
                value: 'No',
                additionalData: {
                  cost: 0,
                  price: 0
                }
              },
              {
                label: 'VPN with MFA',
                value: 'VPN with MFA',
                additionalData: {
                  cost: 16.65,
                  price: 33.31
                }
              },
              {
                label: 'Azure Virtual Desktop with MFA',
                value: 'Azure Virtual Desktop with MFA',
                additionalData: {
                  cost: 24.98,
                  price: 49.96
                }
              },
              {
                label: 'Remote Desktop with MFA',
                value: 'Remote Desktop with MFA',
                additionalData: {
                  cost: 37.47,
                  price: 74.94
                }
              }
            ],
            rowTotalConfig: ['cost', 'price']
          }
        ],
        totalCalculator: calculateTotalWithoutMultiplier
      }
    ]
  },
  {
    categoryName: 'Managed Network',
    categoryId: 'managedNetwork',
    isFinalTotalVisible:true,
    categoryTotalCalculator: sectionTotals => {
      return Object.values(sectionTotals).reduce(
        (accumulator, currentArray) => {
          accumulator[0] += currentArray[0];
          accumulator[1] += currentArray[1];
          return accumulator;
        },
        [0, 0]
      );
    },
    sections: [
      {
        id: 'managedNetwork',
        defaultTotals: [0, 0],
        numberField: {
          label: 'How many sites / physical locations will be managed?',
          id: 'howManySitesPhysicalLocationsWillBeManaged?',
          defaultValue: 1,
          quoteLabel: 'Physical Locations'
        },
        items: [
          {
            label: 'What best describes the physical network setup?',
            id: 'WhatBestDescribesPhysicalNetworkSetup',
            options: [
              {
                label: 'Clean - tidy wiring, well documented',
                value: 'Clean - tidy wiring, well documented',
                additionalData: {
                  cost: 8.33,
                  price: 16.65
                }
              },
              {
                label: 'Standard - some loose wiring, passwords documented',
                value: 'Standard - some loose wiring, passwords documented',
                additionalData: {
                  cost: 16.65,
                  price: 33.3
                }
              }
            ],
            rowTotalConfig: ['cost', 'price']
          },
          {
            label: 'Are the networks dedicated for the use?',
            id: 'areTheNetworksDedicatedForTheUse',
            options: [
              {
                label: 'Owned, full control',
                value: 'Owned, full control',
                additionalData: {
                  cost: 43.33,
                  price: 86.65
                }
              },
              {
                label: 'Rented, some control',
                value: 'Rented, some control',
                additionalData: {
                  cost: 51.65,
                  price: 103.3
                }
              },
              {
                label: 'Shared workspace (e.g. WeWorks), no control',
                value: 'Shared workspace (e.g. WeWorks), no control',
                additionalData: {
                  cost: 0,
                  price: 0
                }
              }
            ],
            rowTotalConfig: ['cost', 'price']
          },
          {
            label: 'What best describes the current network performance?',
            id: 'whatBestDescribesCurrentNetworkPerformance',
            options: [
              {
                label: 'Meets or exceeds current needs',
                value: 'Meets or exceeds current needs',
                additionalData: {
                  cost: 4.16,
                  price: 8.33
                }
              },
              {
                label: 'Occasional Slowness',
                value: 'Occasional Slowness',
                additionalData: {
                  cost: 24.98,
                  price: 49.96
                }
              }
            ],
            rowTotalConfig: ['cost', 'price']
          }
        ],
        totalCalculator: calculateTotalWithMultiplier
      },
      {
        title: 'Firewall / Security Appliance',
        id: 'firewallOrSecurityAppliance',
        defaultTotals: [0, 0],
        numberField: {
          label: 'Number of firewalls / security appliances',
          id: 'numberFwsaTotal',
          defaultValue: 1,
          quoteLabel: 'Firewalls / Security Appliances'
        },
        items: [
          {
            label: 'What best describes the firewalls / security appliances?',
            id: 'whatBestDescribesTheFirewallsSecurityAppliances',
            options: [
              {
                label: 'Cisco Meraki',
                value: 'Cisco Meraki',
                additionalData: {
                  cost: 46.81,
                  price: 93.62
                }
              },
              {
                label: 'Other Cloud-Managed',
                value: 'Other Cloud-Managed',
                additionalData: {
                  cost: 59.3,
                  price: 118.6
                }
              },
              {
                label: 'Supported Non-Cloud Managed',
                value: 'Supported Non-Cloud Managed',
                additionalData: {
                  cost: 109.26,
                  price: 218.52
                }
              }
            ],
            rowTotalConfig: ['cost', 'price']
          }
        ],
        totalCalculator: calculateTotalWithMultiplier
      },
      {
        title: 'Managed Switch',
        id: 'managedSwitch',
        defaultTotals: [0, 0],
        numberField: {
          label: 'Number of switches',
          id: 'numberOfSwitches',
          defaultValue: 1,
          quoteLabel: 'Switches'
        },
        items: [
          {
            label: 'What best describes the switches?',
            id: 'whatBestDescribesTheSwitches',
            options: [
              {
                label: 'Cisco Meraki',
                value: 'Cisco Meraki',
                additionalData: {
                  cost: 30.16,
                  price: 60.31
                }
              },
              {
                label: 'Other Cloud-Managed',
                value: 'Other Cloud-Managed',
                additionalData: {
                  cost: 46.81,
                  price: 93.62
                }
              },
              {
                label: 'Supported Non-Cloud Managed',
                value: 'Supported Non-Cloud Managed',
                additionalData: {
                  cost: 75.95,
                  price: 151.9
                }
              }
            ],
            rowTotalConfig: ['cost', 'price']
          }
        ],
        totalCalculator: calculateTotalWithMultiplier
      },
      {
        title: 'Wireless Access Points',
        id: 'wirelessAccessPoints',
        defaultTotals: [0, 0],
        numberField: {
          label: 'Number of access points',
          id: 'numberOfAccessPoints',
          defaultValue: 1,
          quoteLabel: 'Access Points'
        },
        items: [
          {
            label: 'What best describes the access points?',
            id: 'whatBestDescribesTheAccessPoints',
            options: [
              {
                label: 'Cisco Meraki',
                value: 'Cisco Meraki',
                additionalData: {
                  cost: 4.16,
                  price: 8.33
                }
              },
              {
                label: 'Other Cloud-Managed',
                value: 'Other Cloud-Managed',
                additionalData: {
                  cost: 8.33,
                  price: 16.65
                }
              },
              {
                label: 'Supported Non-Cloud Managed',
                value: 'Supported Non-Cloud Managed',
                additionalData: {
                  cost: 16.65,
                  price: 33.31
                }
              }
            ],
            rowTotalConfig: ['cost', 'price']
          }
        ],
        totalCalculator: calculateTotalWithMultiplier
      }
    ]
  },
  {
    categoryName: 'Modern Workplace (Managed M365)',
    categoryId: 'modernWorkplace',
    isFinalTotalVisible:true,
    categoryTotalCalculator: sectionTotals => {
      return Object.values(sectionTotals).reduce(
        (accumulator, currentArray) => {
          accumulator[0] += currentArray[0];
          accumulator[1] += currentArray[1];
          return accumulator;
        },
        [0, 0]
      );
    },
    sections: [
      {
        title: 'General',
        id: 'general',
        defaultTotals: [0, 0],
        numberField: {
          label: 'Number of active Microsoft 365 Accounts*',
          id: 'numberOfActiveMicrosoft365Accounts',
          defaultValue: 1,
          quoteLabel: 'Microsoft 365 Accounts',
          customValidation: value => {
            return value <= 0
              ? 'Number of active Microsoft 365 Accounts must be greater than 0'
              : false;
          }
        },
        items: [
          {
            label: 'Is MFA enforced for users and administrators?*',
            id: 'isMFAEnforcedForUsersAndAdministrators',
            options: [
              {
                label: 'Yes - via Conditonal Access Policies',
                value: 'Yes - via Conditonal Access Policies',
                additionalData: {
                  cost: 3.11,
                  price: 6.22
                }
              },
              {
                label: 'Yes - via Basic MFA or Security Defaults',
                value: 'Yes - via Basic MFA or Security Defaults',
                additionalData: {
                  cost: 6.22,
                  price: 12.45
                }
              },
              {
                label: 'No - with an approved remediation project',
                value: 'No - with an approved remediation project',
                additionalData: {
                  cost: 6.22,
                  price: 12.45
                }
              }
            ],
            rowTotalConfig: ['cost', 'price']
          }
        ],
        totalCalculator: calculateTotalWithMultiplier
      },
      {
        title: 'Email',
        id: 'email',
        defaultTotals: [0, 0],
        numberField: {
          label: 'Number of active user mailboxes*',
          id: 'numberOfActiveUserMailboxes',
          defaultValue: 1,
          customValidation: value => {
            return value <= 0
              ? 'Number of active user mailboxes must be greater than 0'
              : false;
          }
        },
        items: [
          {
            label: 'How will accounts be protected?*',
            id: 'howWillAccountsBeProtected?',
            options: [
              {
                label: 'FM Provided Advanced Threat Protection',
                value: 'FM Provided Advanced Threat Protection',
                additionalData: {
                  cost: 2.4,
                  price: 4.8
                }
              },
              {
                label:
                  'Other - Client provided alternative confirmed functional',
                value:
                  'Other - Client provided alternative confirmed functional',
                additionalData: {
                  cost: 0,
                  price: 0
                }
              }
            ],
            rowTotalConfig: ['cost', 'price']
          }
        ],
        totalCalculator: calculateTotalWithMultiplier
      },
      {
        title: 'File Sharing and Collaboration',
        id: 'fileSharingAndCollaboration',
        defaultTotals: [0, 0],
        items: [
          {
            label: 'Where is the primary file storage?*',
            id: 'whereIsThePrimaryFileStorage',
            options: [
              {
                label: 'Microsoft 365 (Sharepoint, OneDrive, Azure Files)',
                value: 'Microsoft 365 (Sharepoint, OneDrive, Azure Files)',
                additionalData: {
                  cost: 3.11,
                  price: 6.22
                }
              },
              {
                label: 'Other (On-Premises Shares, Dropbox)',
                value: 'Other (On-Premises Shares, Dropbox)',
                additionalData: {
                  cost: 4.98,
                  price: 9.96
                }
              }
            ],
            rowTotalConfig: ['cost', 'price']
          }
        ],
        totalCalculator: calculateTotalForFileSharingAndCollaboration
      }
    ]
  },
  {
    categoryName: 'Backups',
    categoryId: 'backups',
    isFinalTotalVisible:true,
    categoryTotalCalculator: sectionTotals => {
      return Object.values(sectionTotals).reduce(
        (accumulator, currentArray) => {
          accumulator[0] += currentArray[0];
          accumulator[1] += currentArray[1];
          return accumulator;
        },
        [0, 0]
      );
    },
    sections: [
      {
        title: 'Microsoft 365 Backups',
        id: 'microsoft365Backups',
        defaultTotals: [0, 0],
        numberField: {
          label: 'Number of active Microsoft 365 user Accounts*',
          id: 'numberOfActiveMicrosoft365UserAccounts',
          defaultValue: 1,
          customValidation: value => {
            return value <= 0
              ? 'Number of active Microsoft 365 user Accounts must be greater than 0'
              : false;
          }
        },
        premiumValidation: (selections) => {
          const relationshipLevel = get(
              selections,
              'customerSuccessRelationship.customerSuccessRelationship.relationshipLevel',
              ''
          );

          if (
              relationshipLevel === 'Premium / Legal'
          ) {
            return true;
          }
          return false;
        },
        items: [
          {
            label: 'How will accounts be backed up?*',
            id: 'howWillAccountsBeBackedUp',
            options: [
              {
                label: 'FM Provided Microsoft 365 backups',
                value: 'FM Provided Microsoft 365 backups',
                additionalData: {
                  cost: 1.6,
                  price: 3.2
                },
                premiumAdditionalData: {
                    cost: 3.50,
                    price: 7.00
                  }
              },
              {
                label:
                  'Other - Client provided alternative confirmed functional',
                value:
                  'Other - Client provided alternative confirmed functional',
                additionalData: {
                  cost: 1.6,
                  price: 3.2
                },
                premiumAdditionalData: {
                  cost: 3.50,
                  price: 7.00
                  }
              }
            ],
            rowTotalConfig: ['cost', 'price']
          }
        ],
        totalCalculator: calculateTotalWithMultiplier
      },
      {
        title: 'Server Backups',
        id: 'serverBackups',
        defaultTotals: [0, 0],
        numberField: {
          label: 'Number of servers to backup',
          id: 'numberOfServersToBackup',
          defaultValue: 1,
          quoteLabel: 'Monitored Server Backups'
        },
        items: [
          {
            label: 'Select the backup vendor',
            note:
              'Note: this selection is related to backup management only, please enter any applicable monthly fees in the below "Additional Services" section',
            id: 'selectTheBackupVendor',
            options: [
              {
                label: 'Datto',
                value: 'Datto',
                additionalData: {
                  cost: 4.16,
                  price: 8.33
                }
              },
              {
                label: 'Azure Backups',
                value: 'Azure Backups',
                additionalData: {
                  cost: 8.33,
                  price: 16.65
                }
              },
              {
                label: 'Veeam',
                value: 'Veeam',
                additionalData: {
                  cost: 8.33,
                  price: 16.65
                }
              },
              {
                label: 'StorageCraft',
                value: 'StorageCraft',
                additionalData: {
                  cost: 8.33,
                  price: 16.65
                }
              }
            ],
            rowTotalConfig: ['cost', 'price']
          }
        ],
        totalCalculator: calculateTotalWithMultiplier
      }
    ]
  }
];


export const additionalServicesSchema = [
  {
    categoryName: 'Additional Monthly Services',
    isTitelVisible: true,
    categoryId: 'additionalServices',
    isFinalTotalVisible:false,
    // note: 'E.g., Datto monthly service fees, additional onboarding costs, custom adjustments from deal review',
    categoryTotalCalculator: sectionTotals => {
      return Object.values(sectionTotals).reduce(
        (accumulator, currentArray) => {
          accumulator[0] += currentArray[0];
          accumulator[1] += currentArray[1];
          return accumulator;
        },
        [0, 0]
      );
    },
    sections: [
      {
        id: 'additionalServices',
        defaultTotals: [0, 0],
        title: 'Description',
        customInputs: {
          message: 'Add Additional Service',
          buttonText: 'Add Service',
          buttonTextAdd: 'Add',
          inputFields: [
            {
              label: 'Enter a description of the service',
              id: 'serviceDescription',
              type: 'text',
              equivalentCol: 'desc'
            },
            {
              label: 'Enter cost of the service',
              id: 'serviceCost',
              type: 'Number',
              equivalentCol: 'cost'
            },
            {
              label: 'Enter selling price of the service',
              id: 'servicePrice',
              type: 'Number',
              equivalentCol: 'price'
            }
          ]
        },
        totalCalculator: calculateTotalWithCustomSections
      }
    ]
  }
];

const withoutMultiplierCalculatorObj = {
    'Customer Success (Relationship)':calculateTotalWithoutMultiplier,
    'Servers - Remote Access':calculateTotalWithoutMultiplier,
    'File Sharing and Collaboration':calculateTotalForFileSharingAndCollaboration
};

const customValidationsObj = {
    'User Security & Management':value => {
        return value <= 0
          ? 'Number of Users must be greater than 0'
          : false;
      },
      'Windows Devices': (values, selections, numberSelections) => {
        
        const valueWindows = +get(
          numberSelections,
          'managedUserAndEndpoints.windowsDevices.numberOfWindowsPcs',
          0
        );
        const valueMacs = +get(
          numberSelections,
          'managedUserAndEndpoints.macDevices.numberOfWindowsMacs',
          0
        );

        if (valueMacs === 0 && valueWindows <= 0) {
          return 'Number of Windows PCs must be greater than 0 if number of macs are 0';
        }
        return false;
      },
      'Mac Devices' : (values, selections, numberSelections) => {

        const valueWindows = +get(
          numberSelections,
          'managedUserAndEndpoints.windowsDevices.numberOfWindowsPcs',
          0
        );
        const valueMacs = +get(
          numberSelections,
          'managedUserAndEndpoints.macDevices.numberOfWindowsMacs',
          0
        );

        if (valueMacs <= 0 && valueWindows === 0) {
          return 'Number of macs  must be greater than 0 if number of Windows PCs are 0';
        }
        return false;
      },
      'General': value => {
        return value <= 0
          ? 'Number of active Microsoft 365 Accounts must be greater than 0'
          : false;
      },
      'Email':value => {
        return value <= 0
          ? 'Number of active user mailboxes must be greater than 0'
          : false;
      },
      'Microsoft 365 Backups':value => {
        return value <= 0
          ? 'Number of active Microsoft 365 user Accounts must be greater than 0'
          : false;
      },
}

const customGlobalValidationsObj = {
    'Customer Success (Relationship)': (selections, numberSelections) => {
        const relationshipLevel = get(
          selections,
          'customerSuccessRelationship.customerSuccessRelationship.relationshipLevel',
          ''
        );
        const numberOfUsers = +get(
          numberSelections,
          'managedUserAndEndpoints.userSecurityAndManagement.numberOfUsers',
          0
        );
        if (
          relationshipLevel === 'Low - Monthly SDR, Annual Business Review' &&
          numberOfUsers > 20
        ) {
          return 'Low customer relationship level can only be selected for 20 or less users.';
        }
        return false;
      },
};

const premiumValidationsObj = {
    'User Security & Management': (selections) => {
        const relationshipLevel = get(
            selections,
            'customerSuccessRelationship.customerSuccessRelationship.relationshipLevel',
            ''
        );

        if (
            relationshipLevel === 'Premium / Legal'
        ) {
          return true;
        }
        return false;
      },
      'Windows Devices': (selections) => {
        const relationshipLevel = get(
            selections,
            'customerSuccessRelationship.customerSuccessRelationship.relationshipLevel',
            ''
        );

        if (
            relationshipLevel === 'Premium / Legal'
        ) {
          return true;
        }
        return false;
      },
      'Mac Devices':(selections) => {
        const relationshipLevel = get(
            selections,
            'customerSuccessRelationship.customerSuccessRelationship.relationshipLevel',
            ''
        );

        if (
            relationshipLevel === 'Premium / Legal'
        ) {
          return true;
        }
        return false;
      },
      'Microsoft 365 Backups': (selections) => {
        const relationshipLevel = get(
            selections,
            'customerSuccessRelationship.customerSuccessRelationship.relationshipLevel',
            ''
        );

        if (
            relationshipLevel === 'Premium / Legal'
        ) {
          return true;
        }
        return false;
      },
};

const categoryIds = {
    'Customer Success (Relationship)':'customerSuccessRelationship',
    'Managed User & Endpoints':'managedUserAndEndpoints',
    'Managed Server': 'managedServer',
    'Managed Network': 'managedNetwork',
    'Modern Workplace (Managed M365)': 'modernWorkplace',
    'Backups': 'backups'
};
const serviceIds = {
    'Customer Success (Relationship)': 'customerSuccessRelationship',
    'User Security & Management': 'userSecurityAndManagement',
    'Windows Devices': 'windowsDevices',
    'Mac Devices': 'macDevices',
    'Servers - Physical': 'serversPhysical',
    'Servers - Virtual': 'serversVirtual',
    'Servers - Remote Access': 'serversRemoteAccess',
    'Firewall / Security Appliance': 'firewallOrSecurityAppliance',
    'Managed Switch': 'managedSwitch',
    'Wireless Access Points': 'wirelessAccessPoints',
    'General': 'general',
    'Email': 'email',
    'File Sharing and Collaboration': 'fileSharingAndCollaboration',
    'Microsoft 365 Backups': 'microsoft365Backups',
    'Server Backups': 'serverBackups',
    'Managed Network': 'managedNetwork',

};

const questionIds = {
    'Choose the relationship level' : 'relationshipLevel',
    'Number of Users':'numberOfUsers',
    'What best describes the user identity management?':'bestIdentityManagementDescription',
    'Number of Windows PCs':'numberOfWindowsPcs',
    'What best describes the Windows PCs?':'bestWindowsPCsDescription',
    'How are Windows devices managed?':'howAreWindowsDevicesManaged',
    'Number of Macs':'numberOfWindowsMacs',
    'What best describes the Macs?':'bestMacsDescription',
    'How are Mac devices managed?':'howAreMacDevicesManaged',
    'Number of physical servers':'numberOfPhysicalServers',
    'What platform are physical servers running?':'whatPlatformArePhysicalServersRunning',
    'Number of Virtual Servers/Machines (VM)':'numberOfVirtualServers',
    'What is the oldest OS virtual server running?':'oldestOsVirtualServersRunning',
    'Where are VMs located?':'whereAreVmsLocated',
    'Are any server-based resources accessible from outside the office?':'areAnyServerBasedResourcesAccessibleFromOutsideTheOffice',
    'How many sites / physical locations will be managed?':'howManySitesPhysicalLocationsWillBeManaged?',
    'What best describes the physical network setup?':'WhatBestDescribesPhysicalNetworkSetup',
    'Are the networks dedicated for the use?':'areTheNetworksDedicatedForTheUse',
    'What best describes the current network performance?':'whatBestDescribesCurrentNetworkPerformance',
    'Number of switches': 'numberOfSwitches',
    'What best describes the switches?': 'whatBestDescribesTheSwitches',
    'Number of access points':'numberOfAccessPoints',
    'What best describes the access points?':'whatBestDescribesTheAccessPoints',
    'Number of active Microsoft 365 Accounts':'numberOfActiveMicrosoft365Accounts',
    'Is MFA enforced for users and administrators?':'isMFAEnforcedForUsersAndAdministrators',
    'Number of active user mailboxes':'numberOfActiveUserMailboxes',
    'How will accounts be protected?':'howWillAccountsBeProtected?',
    'Where is the primary file storage?':'whereIsThePrimaryFileStorage',
    'Number of active Microsoft 365 user Accounts':'numberOfActiveMicrosoft365UserAccounts',
    'How will accounts be backed up?':'howWillAccountsBeBackedUp',
    'Number of servers to backup':'numberOfServersToBackup',
    'Select the backup vendor':'selectTheBackupVendor',

};

const quoteLabels = {
    'Number of Users' : 'Users',
    'Number of Windows PCs': 'Windows Devices',
    'Number of Macs' : 'Mac Devices',
    'Number of physical servers' : 'Physical Servers',
    'Number of Virtual Servers/Machines (VM)' : 'Virtual Servers',
    'How many sites / physical locations will be managed?' : 'Physical Locations',
    'Number of firewalls / security appliances' : 'Firewall / Security Appliance',
    'Number of switches' : 'Switches',
    'Number of access points': 'Access Points',
    'Number of active Microsoft 365 Accounts' : 'Microsoft 365 Accounts',
    'Number of servers to backup':'Monitored Server Backups',

};
const excludeQuoteLabels = [
    'Email',
    'Microsoft 365 Backups'
]

export const  transformAPIDataToForm = (apiData)=> {
    return apiData.productCatalogServiceGroups.map(group => ({
      categoryName: group.name.trim(),    // Group name to category name
      categoryId: categoryIds[group.name] ?  categoryIds[group.name] : group.id,        // Group ID to category ID
      isFinalTotalVisible: true,   // Assuming final total visibility
      categoryTotalCalculator: sectionTotals => {
        return Object.values(sectionTotals).reduce((accumulator, currentArray) => {
          accumulator[0] += currentArray[0];
          accumulator[1] += currentArray[1];
          return accumulator;
        }, [0, 0]).map((value) => parseFloat(value.toFixed(2)));
      },
      sections: group.ServicesInGroup.map(service => ({
        title: service.name.trim(),       // Service name 
        id: serviceIds[service.name] ?  serviceIds[service.name] :  service.id,            // Service ID 
        defaultTotals: [0, 0],     // Default totals
        numberField: service.productCatalogQuantities.length > 0 ? {
          label: service.productCatalogQuantities[0].name.trim()+(service.productCatalogQuantities[0].isRequired  ? '*': ''),  // Quantity name
          id: questionIds[service.productCatalogQuantities[0].name] ?  questionIds[service.productCatalogQuantities[0].name] : service.productCatalogQuantities[0].id,       // Quantity ID
          defaultValue: 1 ,
          quoteLabel: service.productCatalogQuantities[0].name && quoteLabels[service.productCatalogQuantities[0].name] ?  quoteLabels[service.productCatalogQuantities[0].name] : !excludeQuoteLabels.includes(service.name) && service.name , // Setting quoteLabel                                 
          customValidation: customValidationsObj[service.name]
        } : null,     
        premiumValidation: premiumValidationsObj[service.name],
        customGlobalValidation:customGlobalValidationsObj[group.name],                                   
        items: service.productCatalogQuestions.map(question => ({
          label: question.name.trim()+(question.isRequired ? '*':''),   // Question name to item label
          id: questionIds[question.name] ?  questionIds[question.name] :  question.id,        // Question ID to item ID
          note: question.note,
          options: question.productCatalogOptions.map(option => ({
            label: option.name,   // Option name to option label
            optionId:option.id,
            value: option.name,     // Option ID as value
            additionalData: {
                cost : option.productCatalogPricings && option.productCatalogPricings[0] && option.productCatalogPricings[0].cost || 0,
                price : option.productCatalogPricings && option.productCatalogPricings[0] && option.productCatalogPricings[0].onetimePrice || 0,  // One-time price from pricing
            },
            premiumAdditionalData:{
                cost : option.productCatalogPricings && option.productCatalogPricings[0] && option.productCatalogPricings[0].costPremium || option.productCatalogPricings && option.productCatalogPricings[0] && option.productCatalogPricings[0].cost,
                price : option.productCatalogPricings && option.productCatalogPricings[0] && option.productCatalogPricings[0].onetimePricePremium || option.productCatalogPricings && option.productCatalogPricings[0] && option.productCatalogPricings[0].onetimePrice,  // Premium One-time price from pricing
            }
          })),
          rowTotalConfig: ['cost', 'price'] // Assuming cost and price are always needed
        })),
        totalCalculator: service.productCatalogQuantities.length > 0 ? calculateTotalWithMultiplier: (withoutMultiplierCalculatorObj[service.name] ?  withoutMultiplierCalculatorObj[service.name] : calculateTotalWithoutMultiplier) // Use the same total calculator
      }))
    }));
  }
  